import { z } from 'zod';
import { envelope, IsoDateTime, Uuid } from './common.js';
import { defineRoute, IdParam } from './route.js';

/**
 * Notion import through the Notion API (ADR-0054). Each person connects their own Notion account
 * with OAuth; on Notion's consent screen they choose which pages the integration may read. Imports
 * run as worker jobs and create the importer's private drafts, like the export ZIP import (ADR-0049).
 */

/** Notion page and database ids, with or without dashes. */
export const NotionId = z.string().regex(/^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i);

export const NotionConnectionView = z.object({
  id: Uuid,
  workspaceId: z.string(),
  workspaceName: z.string().nullable(),
  /** An emoji, or an image address chosen in Notion. */
  workspaceIcon: z.string().nullable(),
  createdAt: IsoDateTime,
  lastUsedAt: IsoDateTime.nullable(),
});
export type NotionConnectionView = z.infer<typeof NotionConnectionView>;

export const NotionStatusView = z.object({
  /** False when this deployment has no Notion integration configured; the feature stays hidden. */
  available: z.boolean(),
  mode: z.enum(['fixture', 'live']).nullable(),
  connection: NotionConnectionView.nullable(),
});
export type NotionStatusView = z.infer<typeof NotionStatusView>;

export const StartNotionConnection = z
  .object({
    /** Where the browser returns after Notion's consent screen: a path of the web app. */
    returnTo: z.string().max(500).optional(),
  })
  .strict();
export type StartNotionConnection = z.infer<typeof StartNotionConnection>;

export const NotionAuthorizeView = z.object({ url: z.url() });
export type NotionAuthorizeView = z.infer<typeof NotionAuthorizeView>;

export const NotionPagesQuery = z.object({
  q: z.string().max(200).optional(),
  cursor: z.string().max(512).optional(),
});
export type NotionPagesQuery = z.infer<typeof NotionPagesQuery>;

export const NotionPageItem = z.object({
  /** Notion id without dashes. */
  id: z.string(),
  kind: z.enum(['page', 'database']),
  title: z.string(),
  icon: z.string().nullable(),
  url: z.string().nullable(),
  /** Where it lives in Notion: at the top of the workspace, or inside a page or database. */
  parent: z.enum(['workspace', 'page', 'database', 'block']).nullable(),
  lastEditedAt: IsoDateTime.nullable(),
});
export type NotionPageItem = z.infer<typeof NotionPageItem>;

export const NotionPageList = z.object({ items: z.array(NotionPageItem), nextCursor: z.string().nullable() });
export type NotionPageList = z.infer<typeof NotionPageList>;

export const StartNotionImport = z
  .object({
    folderId: Uuid,
    /** Pages and databases to import; their subpages come along. */
    pageIds: z.array(NotionId).min(1).max(50),
  })
  .strict();
export type StartNotionImport = z.infer<typeof StartNotionImport>;

export const NotionImportIssue = z.object({ code: z.string(), detail: z.string().optional(), count: z.number().int() });
export type NotionImportIssue = z.infer<typeof NotionImportIssue>;

export const NotionImportReport = z.object({
  documents: z.array(z.object({ id: Uuid, title: z.string(), notionUrl: z.string().nullable(), parentDocumentId: Uuid.nullable(), issues: z.array(NotionImportIssue) })),
  skipped: z.array(z.object({ name: z.string(), reason: z.enum(['limit', 'notShared', 'duplicate', 'cancelled', 'timeLimit', 'interrupted']) })),
  failed: z.array(z.object({ name: z.string(), message: z.string(), documentId: Uuid.nullable() })),
  /** Pages found so far (selected pages and their subpages) and pages finished. */
  total: z.number().int(),
  done: z.number().int(),
  cancelled: z.boolean(),
});
export type NotionImportReport = z.infer<typeof NotionImportReport>;

export const NotionImportView = z.object({
  id: Uuid,
  status: z.enum(['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED', 'CANCELED']),
  progress: z.number(),
  folderId: Uuid,
  safeError: z.string().nullable(),
  report: NotionImportReport,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type NotionImportView = z.infer<typeof NotionImportView>;

/**
 * Not reachable from MCP (see UNEXPOSED_TAGS): connecting needs Notion's consent screen in a
 * browser, and the token is the person's own credential for another company's service.
 */
export const notionRoutes = {
  getNotionConnection: defineRoute({ operationId: 'getNotionConnection', method: 'GET', path: '/integrations/notion', summary: 'Whether the Notion import is available, and your Notion connection', tag: 'integrations', auth: 'session', response: envelope(NotionStatusView), successStatus: 200 }),
  startNotionConnection: defineRoute({ operationId: 'startNotionConnection', method: 'POST', path: '/integrations/notion/authorize', summary: "Start connecting your Notion account: the address of Notion's consent screen", tag: 'integrations', auth: 'session', body: StartNotionConnection, response: envelope(NotionAuthorizeView), successStatus: 200 }),
  disconnectNotion: defineRoute({ operationId: 'disconnectNotion', method: 'DELETE', path: '/integrations/notion', summary: 'Disconnect your Notion account and delete its stored token', tag: 'integrations', auth: 'session', successStatus: 204 }),
  listNotionPages: defineRoute({ operationId: 'listNotionPages', method: 'GET', path: '/integrations/notion/pages', summary: 'Notion pages and databases you shared with the integration', tag: 'integrations', auth: 'session', query: NotionPagesQuery, response: envelope(NotionPageList), successStatus: 200 }),
  startNotionImport: defineRoute({ operationId: 'startNotionImport', method: 'POST', path: '/integrations/notion/imports', summary: 'Import Notion pages into a folder you edit, as your private drafts', tag: 'integrations', auth: 'session', body: StartNotionImport, response: envelope(NotionImportView), successStatus: 202, idempotent: true }),
  getNotionImport: defineRoute({ operationId: 'getNotionImport', method: 'GET', path: '/integrations/notion/imports/:id', summary: 'Progress and report of your Notion import', tag: 'integrations', auth: 'session', params: IdParam, response: envelope(NotionImportView), successStatus: 200 }),
};
